/**
 * Back Roads: the rules of a trip, as pure functions.
 *
 * Everything the screen needs to decide is here and nothing here touches the
 * DOM, the map, or the network, so the whole mode can be played through in a
 * test with a list of coordinates and answers.
 *
 * THREE RULES, AND WHY.
 *
 *  1. NOTHING IS TIMED. The people this mode is for are slower on a phone
 *     than they are in their heads, and a clock measures the phone.
 *
 *  2. NOTHING IS LOST. Every stop pays something, a pin anywhere in the state
 *     pays something, and the total only climbs. A wrong answer still ends
 *     with the postcard, which is the thing the stop was for.
 *
 *  3. A PIN IS SCORED BY DISTANCE, IN BANDS WITH WORDS. "Twelve miles off,
 *     close enough to wave" is a result somebody can feel good about; "312
 *     points" is not. The bands are wide because a thumb on a phone is wide.
 */

import { cityDistanceKm } from '@geogame/shared';
import type { LngLat } from '@geogame/shared';
import { placeById, tripById } from '@geogame/data/backroads';
import type { Question, Stop, Trip } from '@geogame/data/backroads';

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

const KM_PER_MILE = 1.609344;

export function distanceMiles(a: LngLat, b: LngLat): number {
  return cityDistanceKm(a, b) / KM_PER_MILE;
}

export interface PinBand {
  /** Inclusive upper edge, in miles. */
  readonly maxMiles: number;
  readonly points: number;
  readonly verdict: string;
}

/** Widest first is wrong; nearest first, and the last band catches everything. */
export const PIN_BANDS: readonly PinBand[] = [
  { maxMiles: 5, points: 100, verdict: 'Right on the nose' },
  { maxMiles: 15, points: 80, verdict: 'Close enough to wave' },
  { maxMiles: 35, points: 55, verdict: 'In the neighbourhood' },
  { maxMiles: 75, points: 30, verdict: 'Down the road a way' },
  { maxMiles: Number.POSITIVE_INFINITY, points: 10, verdict: 'Well, you found New York' },
];

export const PIN_MAX_POINTS = 100;
export const QUESTION_MAX_POINTS = 100;

export interface PinResult {
  readonly miles: number;
  readonly points: number;
  readonly verdict: string;
}

export function scorePin(pin: LngLat, target: LngLat): PinResult {
  const miles = distanceMiles(pin, target);
  const band = PIN_BANDS.find((b) => miles <= b.maxMiles) ?? (PIN_BANDS[PIN_BANDS.length - 1] as PinBand);
  return { miles, points: band.points, verdict: band.verdict };
}

/** What the player answered, by question kind. */
export type Answer =
  | { readonly kind: 'choice'; readonly index: number }
  | { readonly kind: 'truefalse'; readonly value: boolean }
  | { readonly kind: 'estimate'; readonly value: number }
  | { readonly kind: 'order'; readonly order: readonly number[] };

export interface QuestionResult {
  readonly points: number;
  readonly correct: boolean;
  /** A line for the reveal: what was right, in words. */
  readonly verdict: string;
}

/**
 * An estimate pays in rings around the answer. Inside the tolerance is
 * right; twice it is close; three times it is a fair try; anything else
 * still pays a little for having had a go.
 */
export function scoreEstimate(value: number, answer: number, tolerance: number): QuestionResult {
  const off = Math.abs(value - answer);
  if (off <= tolerance) return { points: 100, correct: true, verdict: 'Right' };
  if (off <= tolerance * 2) return { points: 60, correct: false, verdict: 'Close' };
  if (off <= tolerance * 3) return { points: 30, correct: false, verdict: 'Not far off' };
  return { points: 10, correct: false, verdict: 'A fair try' };
}

/** An ordering pays for every item in its right place. */
export function scoreOrder(order: readonly number[], count: number): QuestionResult {
  let right = 0;
  for (let i = 0; i < count; i += 1) if (order[i] === i) right += 1;
  if (right === count) return { points: 100, correct: true, verdict: 'All in order' };
  const points = Math.max(10, Math.round((100 * right) / count));
  return { points, correct: false, verdict: `${right} of ${count} in the right place` };
}

export function scoreQuestion(question: Question, answer: Answer): QuestionResult {
  switch (question.kind) {
    case 'choice': {
      const correct = answer.kind === 'choice' && answer.index === question.answer;
      return { points: correct ? 100 : 10, correct, verdict: correct ? 'Right' : 'Not this time' };
    }
    case 'truefalse': {
      const correct = answer.kind === 'truefalse' && answer.value === question.answer;
      return { points: correct ? 100 : 10, correct, verdict: correct ? 'Right' : 'Not this time' };
    }
    case 'estimate': {
      if (answer.kind !== 'estimate') return { points: 10, correct: false, verdict: 'A fair try' };
      return scoreEstimate(answer.value, question.answer, question.tolerance);
    }
    case 'order': {
      if (answer.kind !== 'order') return { points: 10, correct: false, verdict: 'A fair try' };
      return scoreOrder(answer.order, question.items.length);
    }
  }
}

// ---------------------------------------------------------------------------
// Presenting a question
// ---------------------------------------------------------------------------

function seedNumber(seed: string): number {
  let h = 2_166_136_261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16_777_619);
  }
  return h >>> 0;
}

function randomFrom(seed: string): () => number {
  let state = seedNumber(seed) || 1;
  return () => {
    state = (Math.imul(state, 1_664_525) + 1_013_904_223) >>> 0;
    return state / 0x1_0000_0000;
  };
}

/** A permutation of 0..n-1, stable for a seed. `perm[k]` is the source index shown at position k. */
export function shuffledIndices(n: number, seed: string): readonly number[] {
  const random = randomFrom(seed);
  const perm = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [perm[i], perm[j]] = [perm[j] as number, perm[i] as number];
  }
  return perm;
}

/**
 * The options as shown: shuffled, so the right answer is never "always the
 * first one" the way it is in the source, and stable for the stop so the
 * buttons do not rearrange under a thumb.
 */
export function presentChoice(
  question: Extract<Question, { kind: 'choice' }>,
  seed: string,
): { readonly options: readonly string[]; readonly answerIndex: number } {
  const perm = shuffledIndices(question.options.length, seed);
  const options = perm.map((i) => question.options[i] as string);
  return { options, answerIndex: perm.indexOf(question.answer) };
}

/** An ordering's items as first shown, with the permutation to map back. */
export function presentOrder(
  question: Extract<Question, { kind: 'order' }>,
  seed: string,
): { readonly items: readonly string[]; readonly perm: readonly number[] } {
  let perm = shuffledIndices(question.items.length, seed);
  // Never show it already solved.
  if (perm.every((v, i) => v === i) && perm.length > 1) perm = [...perm.slice(1), perm[0] as number];
  return { items: perm.map((i) => question.items[i] as string), perm };
}

// ---------------------------------------------------------------------------
// The trip, as a state machine
// ---------------------------------------------------------------------------

export type Phase =
  /** Waiting for a tap on the map. */
  | 'pin'
  /** A pin is down and the player is asked whether it is where they meant. */
  | 'confirm'
  /** The pin is scored and the answer is shown. */
  | 'pinned'
  /** The question is up. */
  | 'question'
  /** The question is scored; the postcard is showing. */
  | 'answered'
  /** Every stop is done. */
  | 'done';

export interface StopRecord {
  readonly stopIndex: number;
  readonly pin: LngLat;
  readonly pinResult: PinResult;
  readonly answer: Answer;
  readonly questionResult: QuestionResult;
}

export interface TripState {
  readonly trip: Trip;
  readonly stopIndex: number;
  readonly phase: Phase;
  /** Where the pin is, from the tap onward. */
  readonly pin: LngLat | null;
  readonly pinResult: PinResult | null;
  readonly answer: Answer | null;
  readonly questionResult: QuestionResult | null;
  /** Finished stops, in order. */
  readonly log: readonly StopRecord[];
}

export function currentStop(state: TripState): Stop {
  const stop = state.trip.stops[state.stopIndex];
  if (!stop) throw new Error(`backroads: no stop ${state.stopIndex} on ${state.trip.id}`);
  return stop;
}

export function startTrip(trip: Trip): TripState {
  return { trip, stopIndex: 0, phase: 'pin', pin: null, pinResult: null, answer: null, questionResult: null, log: [] };
}

export function startTripById(id: string): TripState | null {
  const trip = tripById(id);
  return trip ? startTrip(trip) : null;
}

/** A tap while pinning, or while confirming (which moves the pin). */
export function placePin(state: TripState, lngLat: LngLat): TripState {
  if (state.phase !== 'pin' && state.phase !== 'confirm') return state;
  return { ...state, pin: lngLat, phase: 'confirm' };
}

/** "Yes, that is my spot." */
export function confirmPin(state: TripState): TripState {
  if (state.phase !== 'confirm' || !state.pin) return state;
  const target = placeById(currentStop(state).place).lngLat;
  return { ...state, phase: 'pinned', pinResult: scorePin(state.pin, target) };
}

/** "Let me move it." Back to waiting for a tap; the old pin stays visible until the next one lands. */
export function movePin(state: TripState): TripState {
  if (state.phase !== 'confirm') return state;
  return { ...state, phase: 'pin' };
}

export function askQuestion(state: TripState): TripState {
  if (state.phase !== 'pinned') return state;
  return { ...state, phase: 'question' };
}

export function answerQuestion(state: TripState, answer: Answer): TripState {
  if (state.phase !== 'question') return state;
  const result = scoreQuestion(currentStop(state).question, answer);
  return { ...state, phase: 'answered', answer, questionResult: result };
}

/** On to the next stop, or the summary when there is none. */
export function nextStop(state: TripState): TripState {
  if (state.phase !== 'answered' || !state.pin || !state.pinResult || !state.answer || !state.questionResult) {
    return state;
  }
  const record: StopRecord = {
    stopIndex: state.stopIndex,
    pin: state.pin,
    pinResult: state.pinResult,
    answer: state.answer,
    questionResult: state.questionResult,
  };
  const log = [...state.log, record];
  const last = state.stopIndex + 1 >= state.trip.stops.length;
  return {
    ...state,
    log,
    stopIndex: last ? state.stopIndex : state.stopIndex + 1,
    phase: last ? 'done' : 'pin',
    pin: null,
    pinResult: null,
    answer: null,
    questionResult: null,
  };
}

/** Points banked so far, counting the stop in progress once it is scored. */
export function totalPoints(state: TripState): number {
  let total = 0;
  for (const r of state.log) total += r.pinResult.points + r.questionResult.points;
  if (state.phase !== 'done') {
    total += state.pinResult?.points ?? 0;
    total += state.questionResult?.points ?? 0;
  }
  return total;
}

export function maxPoints(trip: Trip): number {
  return trip.stops.length * (PIN_MAX_POINTS + QUESTION_MAX_POINTS);
}

/**
 * Stars for a finished trip. Generous on purpose: a trip where every pin was
 * "close enough to wave" and every question was right is three stars, and
 * one where half the pins were down the road is still two.
 */
export function stars(points: number, max: number): 1 | 2 | 3 {
  if (max <= 0) return 1;
  const share = points / max;
  if (share >= 0.8) return 3;
  if (share >= 0.55) return 2;
  return 1;
}

/** One line to hand to the family. */
export function shareLine(state: TripState): string {
  const points = totalPoints(state);
  const max = maxPoints(state.trip);
  const s = stars(points, max);
  const stops = state.trip.stops.length;
  return `Back Roads: ${state.trip.title}, ${state.trip.year}. ${'★'.repeat(s)}${'☆'.repeat(3 - s)} ${points} of ${max} over ${stops} stops.`;
}

/** Miles, said the way a person says them. */
export function formatMiles(miles: number): string {
  if (miles < 1) return 'less than a mile';
  const n = Math.round(miles);
  return `${n} mile${n === 1 ? '' : 's'}`;
}
